import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { Toaster } from '@/components/ui/toaster';
import { OnboardingTour } from '@/components/onboarding/onboarding-tour';
import { CommandPalette } from '@/components/ui/command-palette';
import { SocketInitializer } from '@/hooks/use-socket';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { LoginPage } from '@/pages/login';
import { RegisterPage } from '@/pages/register';
import { ForgotPasswordPage } from '@/pages/forgot-password';
import { ResetPasswordPage } from '@/pages/reset-password';
import { NotFoundPage } from '@/pages/not-found';

// Eagerly loaded (critical path — every user sees these)
import { DashboardPage } from '@/pages/dashboard';
import { ProjectsPage } from '@/pages/projects';
import { ProjectDetailPage } from '@/pages/project-detail';
import { TasksPage } from '@/pages/tasks';
import { TicketsPage } from '@/pages/tickets';
import { TicketDetailPage } from '@/pages/ticket-detail';
import { CalendarPage } from '@/pages/calendar';
import { ActivityPage } from '@/pages/activity';
import { ProfilePage } from '@/pages/profile';
import { AuthCallbackPage } from '@/pages/auth-callback';
import { SetPasswordPage } from '@/pages/set-password';
import { AcceptQuotePage } from '@/pages/accept-quote';

// Lazy loaded (secondary pages — loaded on demand)
const LeadsPage = lazy(() => import('@/pages/leads').then(m => ({ default: m.LeadsPage })));
const QuotesPage = lazy(() => import('@/pages/quotes').then(m => ({ default: m.QuotesPage })));
const InvoicesPage = lazy(() => import('@/pages/invoices').then(m => ({ default: m.InvoicesPage })));
const ClientsPage = lazy(() => import('@/pages/clients').then(m => ({ default: m.ClientsPage })));
const ClientDetailPage = lazy(() => import('@/pages/client-detail').then(m => ({ default: m.ClientDetailPage })));
const TimeTrackingPage = lazy(() => import('@/pages/time-tracking').then(m => ({ default: m.TimeTrackingPage })));
const ReportsPage = lazy(() => import('@/pages/reports').then(m => ({ default: m.ReportsPage })));
const SettingsPage = lazy(() => import('@/pages/settings').then(m => ({ default: m.SettingsPage })));
const UsersPage = lazy(() => import('@/pages/users').then(m => ({ default: m.UsersPage })));
const SiteMonitoringPage = lazy(() => import('@/pages/site-monitoring').then(m => ({ default: m.SiteMonitoringPage })));
const InviteAnalyticsPage = lazy(() => import('@/pages/invite-analytics').then(m => ({ default: m.InviteAnalyticsPage })));
const PrivacyPolicyPage = lazy(() => import('@/pages/privacy-policy').then(m => ({ default: m.PrivacyPolicyPage })));
const HelpPage = lazy(() => import('@/pages/help').then(m => ({ default: m.HelpPage })));
const TermsPage = lazy(() => import('@/pages/terms').then(m => ({ default: m.TermsPage })));

/** Shared loading fallback for lazy pages */
function PageLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-dyndev-green border-t-transparent" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** Route guard that checks both authentication AND role */
function RoleRoute({ children, roles }: { children: React.ReactNode; roles: string[] }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!roles.includes(user?.role || '')) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const { isAuthenticated, fetchUser } = useAuthStore();

  // Validate a persisted session at boot: /auth/me now returns 401 for
  // deactivated accounts, so a stale persisted session (from before a
  // deactivation) is cleaned up instead of leaving the user on a broken
  // dashboard until their access token expires. Runs only once per auth state
  // change; after login it also pulls the freshest user profile.
  useEffect(() => {
    if (isAuthenticated) {
      fetchUser();
    }
  }, [isAuthenticated, fetchUser]);

  return (
    <>
      <SocketInitializer />
      <CommandPalette open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} />
      <Routes>
        {/* Public routes — standalone error boundary (no sidebar) */}
        <Route
          path="/login"
          element={
            <ErrorBoundary>
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/register"
          element={
            <ErrorBoundary>
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <ErrorBoundary>
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/reset-password/:token"
          element={
            <ErrorBoundary>
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            </ErrorBoundary>
          }
        />

        {/* OAuth callback — public, handles Google sign-in redirect */}
        <Route
          path="/auth/callback"
          element={
            <ErrorBoundary>
              <AuthCallbackPage />
            </ErrorBoundary>
          }
        />

        {/* Invitation pages — public (accessible with or without auth, uses token from URL) */}
        <Route
          path="/set-password"
          element={
            <ErrorBoundary>
              <SetPasswordPage />
            </ErrorBoundary>
          }
        />
        <Route
          path="/accept-quote"
          element={
            <ErrorBoundary>
              <AcceptQuotePage />
            </ErrorBoundary>
          }
        />

        {/* Terms & Conditions — public, viewable by anyone (linked from quote documents & settings) */}
        <Route
          path="/terms"
          element={
            <ErrorBoundary>
              <TermsPage />
            </ErrorBoundary>
          }
        />

        {/* Privacy Policy — public, viewable by anyone (linked from the T&C) */}
        <Route
          path="/privacy-policy"
          element={
            <ErrorBoundary>
              <PrivacyPolicyPage />
            </ErrorBoundary>
          }
        />

        {/* Protected routes — in-layout error boundary (sidebar remains visible) */}
        <Route
          path="/"
          element={
            <ErrorBoundary withinLayout>
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/projects"
          element={
            <ErrorBoundary withinLayout>
              <ProtectedRoute>
                <ProjectsPage />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <ErrorBoundary withinLayout>
              <ProtectedRoute>
                <ProjectDetailPage />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/tasks"
          element={
            <ErrorBoundary withinLayout>
              <ProtectedRoute>
                <TasksPage />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/leads"
          element={
            <ErrorBoundary withinLayout>
              <RoleRoute roles={['admin', 'project_manager']}>
                <Suspense fallback={<PageLoader />}><LeadsPage /></Suspense>
              </RoleRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/quotes"
          element={
            <ErrorBoundary withinLayout>
              <RoleRoute roles={['admin', 'project_manager', 'client']}>
                <Suspense fallback={<PageLoader />}><QuotesPage /></Suspense>
              </RoleRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/invoices" element={<ErrorBoundary withinLayout><RoleRoute roles={['admin', 'project_manager', 'support_staff', 'client']}><Suspense fallback={<PageLoader />}><InvoicesPage /></Suspense></RoleRoute></ErrorBoundary>} />
        <Route
          path="/profile"
          element={
            <ErrorBoundary withinLayout>
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/time"
          element={
            <ErrorBoundary withinLayout>
              <RoleRoute roles={['admin', 'project_manager']}>
                <Suspense fallback={<PageLoader />}><TimeTrackingPage /></Suspense>
              </RoleRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/clients"
          element={
            <ErrorBoundary withinLayout>
              <RoleRoute roles={['admin', 'project_manager']}>
                <Suspense fallback={<PageLoader />}><ClientsPage /></Suspense>
              </RoleRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/clients/:id"
          element={
            <ErrorBoundary withinLayout>
              <RoleRoute roles={['admin', 'project_manager']}>
                <Suspense fallback={<PageLoader />}><ClientDetailPage /></Suspense>
              </RoleRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/tickets"
          element={
            <ErrorBoundary withinLayout>
              <ProtectedRoute>
                <TicketsPage />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/tickets/:id"
          element={
            <ErrorBoundary withinLayout>
              <ProtectedRoute>
                <TicketDetailPage />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/calendar"
          element={
            <ErrorBoundary withinLayout>
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/activity"
          element={
            <ErrorBoundary withinLayout>
              <ProtectedRoute>
                <ActivityPage />
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/reports"
          element={
            <ErrorBoundary withinLayout>
              <RoleRoute roles={['admin', 'project_manager']}>
                <Suspense fallback={<PageLoader />}><ReportsPage /></Suspense>
              </RoleRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/users"
          element={
            <ErrorBoundary withinLayout>
              <RoleRoute roles={['admin']}>
                <Suspense fallback={<PageLoader />}><UsersPage /></Suspense>
              </RoleRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/site-monitoring"
          element={
            <ErrorBoundary withinLayout>
              <RoleRoute roles={['admin', 'project_manager']}>
                <Suspense fallback={<PageLoader />}><SiteMonitoringPage /></Suspense>
              </RoleRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/invitations/analytics"
          element={
            <ErrorBoundary withinLayout>
              <RoleRoute roles={['admin']}>
                <Suspense fallback={<PageLoader />}><InviteAnalyticsPage /></Suspense>
              </RoleRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/help"
          element={
            <ErrorBoundary withinLayout>
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}><HelpPage /></Suspense>
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
        <Route
          path="/settings"
          element={
            <ErrorBoundary withinLayout>
              <RoleRoute roles={['admin']}>
                <Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>
              </RoleRoute>
            </ErrorBoundary>
          }
        />

        {/* 404 — catch-all */}
        <Route path="*" element={<ErrorBoundary><NotFoundPage /></ErrorBoundary>} />
      </Routes>
      <Toaster />
      <OnboardingTour />
    </>
  );
}
